<template>
  <div v-if="visible" class="claude-runner-card" :data-testid="sectionTag">
    <span class="claude-runner-card__shell" :style="shellStyle" :data-state="view.state">
      <span class="claude-runner-card__controls">
        <template v-for="control in controls" :key="control.key">
          <WFlyout
            v-if="control.key === 'pause'"
            :components="pauseChoices"
            position="right"
            :disabled="control.disabled"
            :section-tag="`${sectionTag}-pause-menu`"
            @item-clicked="pauseFor"
          >
            <template #activator="{ props: menuProps }">
              <WButton
                v-bind="menuProps"
                :show-label-tooltip="String(menuProps['aria-expanded']) !== 'true'"
                variant="icon"
                size="small"
                :icon="CONTROL_ICONS[control.icon]"
                :label="control.label"
                :disabled="control.disabled"
                class="claude-runner-card__control"
                :section-tag="`${sectionTag}-${control.key}`"
              />
            </template>
          </WFlyout>
          <WButton
            v-else
            variant="icon"
            size="small"
            :icon="CONTROL_ICONS[control.icon]"
            :label="control.label"
            :disabled="control.disabled"
            class="claude-runner-card__control"
            :section-tag="`${sectionTag}-${control.key}`"
            @click="act(control.key)"
          />
        </template>
      </span>
      <button
        type="button"
        class="claude-runner-card__button"
        :aria-label="ariaLabel"
        :data-testid="runner?.live ? `${sectionTag}-face-open` : `${sectionTag}-face-start`"
        @click="runner?.live ? openDrawer() : act('start')"
      >
        <ClaudeRunnerCardFace :view="view" :section-tag="sectionTag" />
      </button>
      <span v-if="runner?.live" class="claude-runner-card__controls">
        <FleetRunnerActionsMenu
          :runner="runner"
          position="right"
          :section-tag="`${sectionTag}-menu`"
          @history="openDrawer"
        >
          <template #activator="{ props: menuProps }">
            <WButton
              v-bind="menuProps"
              :show-label-tooltip="String(menuProps['aria-expanded']) !== 'true'"
              variant="icon"
              size="small"
              :icon="mdiDotsVertical"
              label="Runner actions"
              class="claude-runner-card__control"
              :section-tag="`${sectionTag}-menu-open`"
            />
          </template>
        </FleetRunnerActionsMenu>
      </span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { mdiClose, mdiDotsVertical, mdiPause, mdiPlay, mdiPowerStandby } from '@mdi/js'

import { useMyRunnerCard } from '@/composables/useMyRunnerCard'
import { tokens } from '@/plugins/vuetify'

import { fleetRunnerLink } from '@/services/claude-fleet'
import type { RunnerCardControl, RunnerCardState } from '@/services/claude-fleet'

import WButton from '@wasabi/actions/WButton.vue'
import WFlyout from '@wasabi/content-displays/WFlyout.vue'
import FleetRunnerActionsMenu from '@/views/claude-fleet/components/FleetRunnerActionsMenu.vue'

import ClaudeRunnerCardFace from './ClaudeRunnerCardFace.vue'

/**
 * The engineer's own runner on every page: play or pause inside it, then its status, which opens the runner's drawer
 * while it runs, then its menu (its /runner window in Claude first, when it has one).
 */
defineProps<{ sectionTag: string }>()

const router = useRouter()
const { runner, view, controls, pauseChoices, visible, act } = useMyRunnerCard()

/**
 * A live runner takes the admin's theme colour (ADX's brand colour while no theme is on), tinted so the dark text
 * reads on any of the twelve. Starting stays gray with off until the runner checks in, so the colour changing is
 * what says it worked; a warning keeps Wasabi's yellow, as its static pill has it.
 */
const NOT_RUNNING = {
  bg: tokens.color.status.neutral.surface_alt,
  text: tokens.color.status.neutral.text
}

const SHELL_COLORS: Record<RunnerCardState, { bg: string; text: string }> = {
  running: {
    bg: 'rgba(var(--env-color, var(--v-theme-primary)), 0.16)',
    text: tokens.color.status.neutral.text
  },
  attention: { bg: tokens.color.status.warning.surface, text: tokens.color.status.warning.text },
  starting: NOT_RUNNING,
  off: NOT_RUNNING
}

const shellStyle = computed(() => {
  const colors = SHELL_COLORS[view.value.state]
  return { backgroundColor: colors.bg, color: colors.text }
})

const CONTROL_ICONS: Record<RunnerCardControl['icon'], string> = {
  power: mdiPowerStandby,
  play: mdiPlay,
  pause: mdiPause,
  cancel: mdiClose
}

const ariaLabel = computed(
  () =>
    `Your runner: ${view.value.label}. ${view.value.lines.filter(line => line !== view.value.label).join('. ')}`
)

/** WFlyout hands back its own item type, so the choice is found again by its key. */
function pauseFor(item: { key?: unknown }) {
  const choice = pauseChoices.value.find(c => c.key === item.key)
  if (choice) void act(choice.key)
}

function openDrawer() {
  if (runner.value)
    void router.push({ name: 'claude-fleet', ...fleetRunnerLink(runner.value.machine) })
}
</script>

<style scoped>
.claude-runner-card {
  display: flex;
  min-width: 0;
  max-width: 100%;
}

.claude-runner-card__shell {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  padding: 0 2px;
  border-radius: 16px;
}

/* The face's own chip goes clear: the shell carries the colour around the buttons and the label alike. */
.claude-runner-card :deep(.wasabi-pill) {
  background-color: transparent !important;
  color: inherit !important;
}

/* The runner menu's wrapper too, which would otherwise keep the pill full width and push the buttons out. */
.claude-runner-card__shell > :deep(span),
.claude-runner-card :deep(.baseElement) {
  display: flex;
  min-width: 0;
}

.claude-runner-card__controls {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
}

/* The label starts as far from the button as it ends from its status icon. */
.claude-runner-card__shell :deep(.wasabi-pill.static.small) {
  padding-left: 1px !important;
}

/* WButton puts the class on its inner v-btn, past this component's scope. */
.claude-runner-card__controls :deep(.claude-runner-card__control) {
  width: 20px !important;
  min-width: 20px !important;
  height: 20px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  background-color: transparent !important;
  /* The pill's own text colour: the pill already says the state. */
  color: inherit !important;
}

.claude-runner-card__controls :deep(.claude-runner-card__control .v-icon) {
  font-size: 16px;
}

.claude-runner-card__button {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: none;
  cursor: pointer;
}

.claude-runner-card__button:focus-visible {
  outline: 2px solid var(--color-input-border-focus, #0071e8);
  outline-offset: 2px;
}
</style>
